/**
 * Frontend Application Insights telemetry.
 * Lazy-loads the SDK only when VITE_APP_INSIGHTS_CONNECTION_STRING is set.
 * Includes Web Vitals (CLS, FID, LCP, FCP, TTFB) reporting.
 */

let appInsights: import('@microsoft/applicationinsights-web').ApplicationInsights | null = null;

export async function initTelemetry(): Promise<void> {
  const connectionString = import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING;
  if (!connectionString) return;

  try {
    const { ApplicationInsights } = await import('@microsoft/applicationinsights-web');

    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxPerfTracking: true,
        disableFetchTracking: false,
      },
    });

    appInsights.loadAppInsights();

    // Report Web Vitals to App Insights
    initWebVitals();
  } catch (err) {
    console.warn('Application Insights failed to load:', err);
  }
}

async function initWebVitals(): Promise<void> {
  try {
    const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import('web-vitals');
    const reportVital = (metric: { name: string; value: number; id: string }) => {
      trackEvent(`WebVital:${metric.name}`, {
        value: String(metric.value),
        id: metric.id,
      });
    };
    onCLS(reportVital);
    onINP(reportVital);
    onLCP(reportVital);
    onFCP(reportVital);
    onTTFB(reportVital);
  } catch {
    // web-vitals not available — non-critical
  }
}

export function trackEvent(name: string, properties?: Record<string, string>): void {
  appInsights?.trackEvent({ name }, properties);
}

export function trackException(error: Error, properties?: Record<string, string>): void {
  appInsights?.trackException({ exception: error }, properties);
}
